import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    AcademicCapIcon,
    ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../components/buttons/Button';
import { ClassSlotCard } from './ClassSlotCard';
import { ModalCreateZoomSession } from './ModalCreateZoomSession';
import './class-slots-panel.css';

export const ClassSlotList = ({
    slots = [],
    latestPastSession,
    canCreate,
    onCreateManual,
    onEndClass,
    onJoin,
    onWatch,
    onSaveExternalLink,
    onOpenDiagnostics,
    canSeeDiagnostics = false,
    loading,
    slotsLoaded,
    zoomConfigured,
    zoomEnabled = false,
    showCost = false,
    joinedSessionId = null,
}) => {
    const offlineMode = !zoomEnabled;
    const showConfigWarning = slotsLoaded && !zoomConfigured && zoomEnabled;
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [pastOpen, setPastOpen] = useState(true);

    if (!slotsLoaded && loading) {
        return (
            <div className="class-slots-panel">
                <div className="class-slots-panel__list">
                    <div className="class-slots-panel__skeleton" aria-label="Cargando clases">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="class-slots-panel__skeleton-card">
                                <div className="w-11 h-11 rounded-lg bg-gray-200 flex-shrink-0" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                                    <div className="h-3 bg-gray-100 rounded w-3/5" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (showConfigWarning && !slots.length && !canCreate) {
        return (
            <div className="class-slots-panel">
                <div className="class-slots-panel__list">
                    <div className="class-slots-panel__warning">
                        <p className="class-slots-panel__warning-text">
                            Zoom no está configurado en el servidor. Verifica las credenciales en{' '}
                            <code>server/.env</code> y reinicia el servidor.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!slots.length && !canCreate) {
        return (
            <div className="class-slots-panel">
                <div className="class-slots-panel__list">
                    <div className="class-slots-panel__empty">
                        <AcademicCapIcon className="class-slots-panel__empty-icon" aria-hidden="true" />
                        <p className="class-slots-panel__empty-text">
                            Aún no hay clases creadas para este curso.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const isLiveZoom = (slot) => Boolean(slot?.hasLiveZoom) || slot?.session?.status === 'live';
    const isSessionActive = (slot) => {
        if (!slot?.session) return false;
        return !['ended', 'cancelled'].includes(slot.session.status);
    };

    const upcoming = slots
        .filter(isSessionActive)
        .sort((a, b) => {
            const aLive = isLiveZoom(a) ? 0 : 1;
            const bLive = isLiveZoom(b) ? 0 : 1;
            if (aLive !== bLive) return aLive - bLive;
            return new Date(a.scheduledStart) - new Date(b.scheduledStart);
        });
    const past = slots
        .filter((s) => !isSessionActive(s))
        .sort((a, b) => new Date(b.scheduledStart) - new Date(a.scheduledStart));

    const hasActiveClass = slots.some(isSessionActive);
    const canShowCreateCard = canCreate && !hasActiveClass;

    const now = new Date();
    const stampDay = format(now, 'd', { locale: es });
    const stampMonth = format(now, 'MMM', { locale: es }).replace('.', '');

    const handleCreateClass = async (payload) => {
        setCreating(true);
        try {
            await onCreateManual?.(payload);
            setCreateModalOpen(false);
        } catch {
            // toast en el layout
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="class-slots-panel">
            <div className="class-slots-panel__list">
                <div className="class-slots-panel__list-inner course-tab-stagger">
                    {canShowCreateCard && (
                        <article className="class-slots-panel__card class-slots-panel__card--compose">
                            <div className="class-slots-panel__date-stamp" aria-hidden="true">
                                <span className="class-slots-panel__date-month">{stampMonth}</span>
                                <span className="class-slots-panel__date-day">{stampDay}</span>
                            </div>

                            <div className="class-slots-panel__card-main">
                                <div className="class-slots-panel__card-body">
                                    <div className="class-slots-panel__title-row">
                                        <h3 className="class-slots-panel__card-title">Nueva clase</h3>
                                        <span className="class-slots-panel__badge class-slots-panel__badge--created">
                                            {offlineMode ? 'Sin Zoom' : 'Zoom'}
                                        </span>
                                    </div>
                                    <p className="class-slots-panel__card-time">
                                        {offlineMode
                                            ? 'Puedes pegar un link de Meet, Zoom, Teams o Webex al crear la clase'
                                            : 'Con Zoom interno y grabación al terminar'}
                                    </p>
                                </div>

                                <div className="class-slots-panel__card-footer">
                                    <Button
                                        type="button"
                                        variant="gradient"
                                        size="sm"
                                        className="class-slots-panel__action-btn"
                                        disabled={creating}
                                        onClick={() => setCreateModalOpen(true)}
                                    >
                                        Crear clase
                                    </Button>
                                </div>
                            </div>
                        </article>
                    )}

                    <ModalCreateZoomSession
                        open={createModalOpen && canShowCreateCard}
                        setOpen={setCreateModalOpen}
                        loading={creating}
                        mode="manual"
                        allowExternalLink={offlineMode}
                        onConfirm={handleCreateClass}
                    />

                    {!slots.length && canShowCreateCard && (
                        <div className="class-slots-panel__empty class-slots-panel__empty--compact">
                            <p className="class-slots-panel__empty-text">
                                Aún no hay clases. Crea la primera arriba.
                            </p>
                        </div>
                    )}

                    {upcoming.length > 0 && (
                        <section>
                            <h4 className="class-slots-panel__section-title">
                                En curso
                                <span className="class-slots-panel__section-count">{upcoming.length}</span>
                            </h4>
                            <div className="class-slots-panel__cards">
                                {upcoming.map((slot) => (
                                    <ClassSlotCard
                                        key={slot.slotKey || slot.session?._id}
                                        slot={slot}
                                        canCreate={canCreate}
                                        onJoin={onJoin}
                                        onEndClass={onEndClass}
                                        onWatch={onWatch}
                                        onSaveExternalLink={onSaveExternalLink}
                                        onOpenDiagnostics={onOpenDiagnostics}
                                        canSeeDiagnostics={canSeeDiagnostics}
                                        loading={loading}
                                        offlineMode={offlineMode}
                                        showCost={showCost}
                                        joinedSessionId={joinedSessionId}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {past.length > 0 && (
                        <section className="class-slots-panel__accordion">
                            <button
                                type="button"
                                className={`class-slots-panel__section-title class-slots-panel__section-title--accordion${pastOpen ? ' is-open' : ''}`}
                                aria-expanded={pastOpen}
                                aria-controls="class-slots-past-panel"
                                id="class-slots-past-trigger"
                                onClick={() => setPastOpen((open) => !open)}
                            >
                                <span className="class-slots-panel__section-title-text">Anteriores</span>
                                <span className="class-slots-panel__section-count">{past.length}</span>
                                <ChevronDownIcon
                                    className="class-slots-panel__section-chevron"
                                    aria-hidden="true"
                                />
                            </button>
                            <div
                                id="class-slots-past-panel"
                                role="region"
                                aria-labelledby="class-slots-past-trigger"
                                className={`class-slots-panel__accordion-panel${pastOpen ? ' is-open' : ''}`}
                                hidden={!pastOpen}
                            >
                                <div className="class-slots-panel__cards">
                                    {past.map((slot) => (
                                        <ClassSlotCard
                                            key={slot.slotKey || slot.session?._id}
                                            slot={slot}
                                            isLatestPast={slot.session?._id === latestPastSession?._id}
                                            canCreate={canCreate}
                                            onJoin={onJoin}
                                            onEndClass={onEndClass}
                                            onWatch={onWatch}
                                            onSaveExternalLink={onSaveExternalLink}
                                            onOpenDiagnostics={onOpenDiagnostics}
                                            canSeeDiagnostics={canSeeDiagnostics}
                                            loading={loading}
                                            offlineMode={offlineMode}
                                            showCost={showCost}
                                            joinedSessionId={joinedSessionId}
                                        />
                                    ))}
                                </div>
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};
